/**
 * InsightsSidebar Component
 *
 * Displays conversation insights in the sidebar including Strengths,
 * Patterns (neutral — context-dependent), and Areas for Growth sections
 * with collapsible views and badge counts.
 *
 * IMPORTANT: This component shows data if it exists. The `insightsStatus` prop
 * is ONLY used to show progress during initial generation when there's no data,
 * NOT to control visibility of existing data.
 */

import { useState, useMemo, useCallback, useEffect } from "react";
import {
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ThumbsUp,
  AlertTriangle,
  Loader2,
  Copy,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  ANNOTATION_TYPE_LABELS,
  type SegmentAnnotationResponse,
  type AnnotationType,
} from "@/types/annotation";
import type { AnalysisComponentStatus } from "@/hooks/useAnalysisStatus";

interface InsightsSidebarProps {
  /** All annotations for the conversation */
  annotations: SegmentAnnotationResponse[];
  /** Whether annotations are loading from API */
  isLoading?: boolean;
  /** Status of insights generation (from pipeline) - only for progress display when no data */
  insightsStatus?: AnalysisComponentStatus;
  /** Callback when an annotation is clicked - receives segment_id to scroll to */
  onAnnotationClick?: (annotation: SegmentAnnotationResponse) => void;
  /** Callback to retry failed generation */
  onRetry?: () => void;
}

/**
 * Mapping of annotation types to their category. Patterns are neutral
 * because they can be positive or negative depending on context.
 */
const ANNOTATION_CATEGORY: Record<
  AnnotationType,
  "strength" | "neutral" | "growth"
> = {
  valid_feeling: "strength",
  insight: "strength",
  positive: "strength",
  pattern: "neutral",
  defense: "growth",
  criticism: "growth",
  blame: "growth",
};

/**
 * Copy-to-clipboard button with visual feedback.
 * Shows Copy icon normally, Check icon for 2s after copying.
 * Hidden if clipboard API is unavailable.
 */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      navigator.clipboard.writeText(text).then(() => setCopied(true));
    },
    [text]
  );

  if (!navigator.clipboard) return null;

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="p-0.5 text-muted-foreground hover:text-foreground transition-colors"
      aria-label={copied ? "Copied" : "Copy insight"}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

/**
 * Badge component for insight counts with navigation arrows on hover
 */
function InsightBadge({
  type,
  annotations,
  variant,
  onNavigate,
}: {
  type: AnnotationType;
  annotations: SegmentAnnotationResponse[];
  variant: "strength" | "neutral" | "growth";
  onNavigate?: (annotation: SegmentAnnotationResponse) => void;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const label = ANNOTATION_TYPE_LABELS[type];
  const count = annotations.length;
  const hasMultiple = count > 1;

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newIndex = currentIndex > 0 ? currentIndex - 1 : count - 1;
    setCurrentIndex(newIndex);
    onNavigate?.(annotations[newIndex]);
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newIndex = currentIndex < count - 1 ? currentIndex + 1 : 0;
    setCurrentIndex(newIndex);
    onNavigate?.(annotations[newIndex]);
  };

  const handleClick = () => {
    onNavigate?.(annotations[currentIndex]);
  };

  const currentAnnotation = annotations[currentIndex];
  const copyText =
    currentAnnotation?.description || currentAnnotation?.title || "";

  const chipStyles = {
    strength:
      "bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900 hover:border-green-300 dark:hover:border-green-700",
    neutral:
      "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100 hover:border-slate-300",
    growth:
      "bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900 hover:border-red-300 dark:hover:border-red-700",
  }[variant];
  const counterStyles = {
    strength: "bg-green-200/60 dark:bg-green-800/60",
    neutral: "bg-slate-200/60",
    growth: "bg-red-200/60 dark:bg-red-800/60",
  }[variant];
  const navStyles = {
    strength:
      "bg-green-100 dark:bg-green-900 border-green-300 dark:border-green-700 hover:bg-green-200 dark:hover:bg-green-800 text-green-700 dark:text-green-300",
    neutral: "bg-slate-100 border-slate-300 hover:bg-slate-200 text-slate-700",
    growth:
      "bg-red-100 dark:bg-red-900 border-red-300 dark:border-red-700 hover:bg-red-200 dark:hover:bg-red-800 text-red-700 dark:text-red-300",
  }[variant];
  const Icon =
    variant === "growth"
      ? AlertTriangle
      : variant === "neutral"
        ? ChevronRight
        : ThumbsUp;

  return (
    <div className="relative group/chip flex items-center gap-0.5">
      <button
        type="button"
        onClick={handleClick}
        className={cn(
          "relative flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all",
          chipStyles
        )}
      >
        <Icon className="w-2.5 h-2.5" />
        <span>
          {label}
          {annotations[currentIndex]?.block_label && (
            <span className="ml-0.5 opacity-70">
              {" "}
              ({annotations[currentIndex].block_label})
            </span>
          )}
        </span>

        {/* Position indicator for multiple instances */}
        {hasMultiple && (
          <span
            className={cn(
              "px-1 py-0.5 rounded-full text-[9px] font-semibold",
              counterStyles
            )}
          >
            {currentIndex + 1}/{count}
          </span>
        )}

        {/* Count badge for single segment with multiple annotations */}
        {!hasMultiple && count > 1 && (
          <span
            className={cn(
              "px-1 py-0.5 rounded-full text-[9px] font-semibold",
              counterStyles
            )}
          >
            {count}
          </span>
        )}
      </button>

      {/* Copy button - appears on hover */}
      <div className="opacity-0 group-hover/chip:opacity-100 transition-opacity">
        <CopyButton text={copyText} />
      </div>

      {/* Navigation arrows - appear on hover when multiple instances */}
      {hasMultiple && (
        <div className="absolute -right-1 top-1/2 -translate-y-1/2 flex gap-0.5 opacity-0 group-hover/chip:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={handlePrev}
            className={cn("p-0.5 rounded-full shadow-sm border", navStyles)}
            aria-label="Previous instance"
          >
            <ChevronLeft className="w-2.5 h-2.5" />
          </button>
          <button
            type="button"
            onClick={handleNext}
            className={cn("p-0.5 rounded-full shadow-sm border", navStyles)}
            aria-label="Next instance"
          >
            <ChevronRight className="w-2.5 h-2.5" />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Collapsible section component
 */
function CollapsibleSection({
  title,
  count,
  children,
  defaultExpanded = true,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="space-y-2.5">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-xs font-semibold text-foreground w-full hover:text-primary transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="w-3.5 h-3.5" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5" />
        )}
        {title}
        <span className="text-[10px] font-normal text-muted-foreground ml-auto">
          {count} found
        </span>
      </button>

      {isExpanded && (
        <div className="flex flex-wrap gap-1.5 pl-1">{children}</div>
      )}
    </div>
  );
}

export function InsightsSidebar({
  annotations,
  isLoading = false,
  insightsStatus,
  onAnnotationClick,
}: InsightsSidebarProps) {
  // Group annotations by category
  const {
    strengthAnnotations,
    neutralAnnotations,
    growthAnnotations,
    totalStrengths,
    totalNeutral,
    totalGrowth,
  } = useMemo(() => {
    const strengthAnnotations: Partial<
      Record<AnnotationType, SegmentAnnotationResponse[]>
    > = {};
    const neutralAnnotations: Partial<
      Record<AnnotationType, SegmentAnnotationResponse[]>
    > = {};
    const growthAnnotations: Partial<
      Record<AnnotationType, SegmentAnnotationResponse[]>
    > = {};
    let totalStrengths = 0;
    let totalNeutral = 0;
    let totalGrowth = 0;

    annotations.forEach((annotation) => {
      const category = ANNOTATION_CATEGORY[annotation.annotation_type];
      if (!category) return;

      const bucket =
        category === "strength"
          ? strengthAnnotations
          : category === "neutral"
            ? neutralAnnotations
            : growthAnnotations;
      if (!bucket[annotation.annotation_type]) {
        bucket[annotation.annotation_type] = [];
      }
      bucket[annotation.annotation_type]!.push(annotation);

      if (category === "strength") totalStrengths++;
      else if (category === "neutral") totalNeutral++;
      else totalGrowth++;
    });

    return {
      strengthAnnotations,
      neutralAnnotations,
      growthAnnotations,
      totalStrengths,
      totalNeutral,
      totalGrowth,
    };
  }, [annotations]);

  // Determine if we have any data
  const hasData = annotations.length > 0;

  // CRITICAL: If we have data, ALWAYS show it regardless of any status
  if (hasData) {
    return (
      <div className="space-y-5">
        {/* Strengths Section */}
        {totalStrengths > 0 && (
          <CollapsibleSection title="Strengths" count={totalStrengths}>
            {Object.entries(strengthAnnotations).map(
              ([type, typeAnnotations]) => (
                <InsightBadge
                  key={type}
                  type={type as AnnotationType}
                  annotations={typeAnnotations!}
                  variant="strength"
                  onNavigate={onAnnotationClick}
                />
              )
            )}
          </CollapsibleSection>
        )}

        {/* Patterns Section (neutral — context-dependent) */}
        {totalNeutral > 0 && (
          <CollapsibleSection title="Patterns" count={totalNeutral}>
            {Object.entries(neutralAnnotations).map(
              ([type, typeAnnotations]) => (
                <InsightBadge
                  key={type}
                  type={type as AnnotationType}
                  annotations={typeAnnotations!}
                  variant="neutral"
                  onNavigate={onAnnotationClick}
                />
              )
            )}
          </CollapsibleSection>
        )}

        {/* Areas for Growth Section */}
        {totalGrowth > 0 && (
          <CollapsibleSection
            title="Areas for Growth"
            count={totalGrowth}
            defaultExpanded={true}
          >
            {Object.entries(growthAnnotations).map(
              ([type, typeAnnotations]) => (
                <InsightBadge
                  key={type}
                  type={type as AnnotationType}
                  annotations={typeAnnotations!}
                  variant="growth"
                  onNavigate={onAnnotationClick}
                />
              )
            )}
          </CollapsibleSection>
        )}
      </div>
    );
  }

  // No data - show loading skeleton during initial API fetch
  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="h-4 bg-muted animate-pulse rounded" />
        <div className="h-4 bg-muted animate-pulse rounded w-3/4" />
        <div className="h-4 bg-muted animate-pulse rounded w-1/2" />
      </div>
    );
  }

  // No data - show generating state only if actively generating
  if (insightsStatus?.isGenerating) {
    return (
      <div className="flex flex-col items-center justify-center py-4">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-3">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
        <p className="text-sm text-muted-foreground text-center">
          Analyzing conversation patterns...
        </p>
        {insightsStatus.progress > 0 && insightsStatus.progress < 100 && (
          <div className="mt-2 w-full max-w-[120px]">
            <div className="h-1 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-300"
                style={{ width: `${insightsStatus.progress}%` }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // No data and not generating - show empty state
  return (
    <p className="text-sm text-muted-foreground">
      Insights will appear here once the conversation is analyzed.
    </p>
  );
}
